import { useEffect, useRef, useState } from "react";
import { Check, Info, Loader2, Lock, Star } from "lucide-react";
import type { MatchDto } from "@/types/api";
import { TeamFlag } from "@/components/TeamFlag";
import type { TeamRef } from "@/components/TeamStatsModal";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatTime, stageLabel } from "@/lib/stage";

type SaveState = "idle" | "saving" | "saved" | "error";

interface Props {
  match: MatchDto;
  onSubmitTip: (matchId: string, scoreA: number, scoreB: number) => Promise<void>;
  onToggleTuti: (matchId: string) => Promise<void>;
  onOpenTips: (match: MatchDto) => void;
  onOpenTeam?: (team: TeamRef) => void;
  showStage?: boolean;
}

function clampScore(v: string): string {
  const digits = v.replace(/\D/g, "").slice(0, 2);
  return digits;
}

export function MatchCard({ match, onSubmitTip, onToggleTuti, onOpenTips, onOpenTeam, showStage }: Props) {
  const [a, setA] = useState(match.myScoreA?.toString() ?? "");
  const [b, setB] = useState(match.myScoreB?.toString() ?? "");
  const [save, setSave] = useState<SaveState>("idle");
  const [tutiBusy, setTutiBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ha kívülről frissül a meccs (refetch), igazítsuk a mezőket, amíg épp nem gépel a user.
  useEffect(() => {
    if (save === "idle") {
      setA(match.myScoreA?.toString() ?? "");
      setB(match.myScoreB?.toString() ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.myScoreA, match.myScoreB]);

  const hasResult = match.scoreA !== null && match.scoreB !== null;
  const hasTip = a !== "" && b !== "";
  const showTuti = (match.tippable && hasTip) || match.isTuti;

  function scheduleSave(nextA: string, nextB: string) {
    if (timer.current) clearTimeout(timer.current);
    if (nextA === "" || nextB === "") {
      setSave("idle");
      return;
    }
    setSave("saving");
    timer.current = setTimeout(async () => {
      try {
        await onSubmitTip(match.id, Number(nextA), Number(nextB));
        setSave("saved");
        setTimeout(() => setSave("idle"), 1500);
      } catch {
        setSave("error");
      }
    }, 600);
  }

  async function handleTuti() {
    setTutiBusy(true);
    try {
      await onToggleTuti(match.id);
    } finally {
      setTutiBusy(false);
    }
  }

  return (
    <div
      className={cn(
        "rounded-xl border bg-card p-3 shadow-sm transition-colors sm:p-4",
        match.isTuti ? "border-warning/60 ring-1 ring-warning/40" : "border-border",
      )}
    >
      {/* Meta sor */}
      <div className="mb-2.5 flex items-center gap-2 text-xs text-muted-foreground">
        {match.isTuti && (
          <Star className="size-4 fill-warning text-warning" aria-label="Tuti tipp" />
        )}
        {showStage && <Badge variant="outline">{stageLabel(match.stage)}</Badge>}
        <span className="tabular-nums">{formatTime(match.matchDate)}</span>

        <div className="ml-auto flex items-center gap-2">
          {match.points !== null && (
            <Badge variant={match.points > 0 ? "default" : "secondary"}>{match.points} pont</Badge>
          )}
          {showTuti && (
            <button
              type="button"
              onClick={handleTuti}
              disabled={tutiBusy || (!match.tippable && !match.isTuti)}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-50",
                match.isTuti
                  ? "border-warning/60 bg-warning/15 text-warning"
                  : "border-border text-muted-foreground hover:text-foreground",
              )}
              title={match.isTuti ? "Tuti tipp levétele/áthelyezése" : "Megjelölés Tuti tippnek"}
            >
              {tutiBusy ? <Loader2 className="size-3.5 animate-spin" /> : <Star className={cn("size-3.5", match.isTuti && "fill-warning")} />}
              Tuti
            </button>
          )}
        </div>
      </div>

      {/* Csapatok + tipp */}
      <div className="flex items-center gap-2">
        <TeamSide
          name={match.teamAName}
          flag={match.teamAFlag}
          align="right"
          onClick={onOpenTeam ? () => onOpenTeam({ id: match.teamAId, name: match.teamAName, flag: match.teamAFlag }) : undefined}
        />

        <div className="flex shrink-0 items-center gap-1.5">
          {match.tippable ? (
            <>
              <ScoreInput value={a} onChange={(v) => { setA(v); scheduleSave(v, b); }} label={`${match.teamAName} gólja`} />
              <span className="text-muted-foreground">–</span>
              <ScoreInput value={b} onChange={(v) => { setB(v); scheduleSave(a, v); }} label={`${match.teamBName} gólja`} />
            </>
          ) : (
            <div className="flex items-center gap-1.5 text-lg font-semibold tabular-nums">
              <span className="w-9 text-center">{match.myScoreA ?? "–"}</span>
              <span className="text-muted-foreground">–</span>
              <span className="w-9 text-center">{match.myScoreB ?? "–"}</span>
            </div>
          )}
        </div>

        <TeamSide
          name={match.teamBName}
          flag={match.teamBFlag}
          align="left"
          onClick={onOpenTeam ? () => onOpenTeam({ id: match.teamBId, name: match.teamBName, flag: match.teamBFlag }) : undefined}
        />
      </div>

      {/* Állapot / eredmény */}
      {hasResult ? (
        <div className="mt-2.5 flex flex-col items-center gap-1 rounded-lg bg-success/10 py-2 text-success">
          <span className="text-[0.65rem] font-semibold uppercase tracking-wide">Végeredmény</span>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <TeamFlag name={match.teamAName} flag={match.teamAFlag} className="size-5" />
            <span className="tabular-nums">{match.scoreA}–{match.scoreB}</span>
            <TeamFlag name={match.teamBName} flag={match.teamBFlag} className="size-5" />
            <button
              type="button"
              onClick={() => onOpenTips(match)}
              className="ml-1 rounded-full p-1 hover:bg-success/20"
              title="Mások tippjei"
              aria-label="Mások tippjei"
            >
              <Info className="size-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2.5 flex min-h-5 items-center justify-center gap-2 text-xs">
          {!match.tippable ? (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Lock className="size-3.5" /> Fogadás véget ért
            </span>
          ) : (
            <SaveIndicator state={save} />
          )}
        </div>
      )}
    </div>
  );
}

function TeamSide({
  name,
  flag,
  align,
  onClick,
}: {
  name: string;
  flag?: string | null;
  align: "left" | "right";
  onClick?: () => void;
}) {
  const inner = (
    <>
      <TeamFlag name={name} flag={flag} className="size-7" />
      <span className="truncate text-sm font-medium sm:text-base">{name}</span>
    </>
  );
  const base = cn("flex min-w-0 flex-1 items-center gap-2", align === "right" ? "flex-row-reverse text-right" : "text-left");
  if (!onClick) return <div className={base}>{inner}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(base, "rounded-md py-0.5 transition-colors hover:text-primary")}
      title={`${name} statisztikái`}
    >
      {inner}
    </button>
  );
}

function ScoreInput({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return (
    <input
      inputMode="numeric"
      pattern="[0-9]*"
      aria-label={label}
      value={value}
      onChange={(e) => onChange(clampScore(e.target.value))}
      className="h-11 w-11 rounded-lg border border-input bg-background text-center text-lg font-semibold tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    />
  );
}

function SaveIndicator({ state }: { state: SaveState }) {
  if (state === "saving")
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" /> Mentés…
      </span>
    );
  if (state === "saved")
    return (
      <span className="inline-flex items-center gap-1 text-success">
        <Check className="size-3.5" /> Mentve
      </span>
    );
  if (state === "error") return <span className="text-destructive">Mentés sikertelen</span>;
  return <span className="text-muted-foreground">Írd be a tipped</span>;
}
